import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Users, Mail, Clock, Trash, Edit, Save, X } from 'lucide-react';
import { visitorService } from '../services/visitorService';

interface Visitor {
  id: number;
  first_name: string;
  email: string;
  timestamp: string;
  userAgent: string;
  visit_count: number;
  last_visit: string;
}

interface EditingVisitor extends Visitor {
  isEditing: boolean;
}

export default function AdminDashboard() {
  const [visitors, setVisitors] = useState<EditingVisitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState<'timestamp' | 'visit_count'>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchVisitors();
  }, []);

  const fetchVisitors = async () => {
    try {
      const data = await visitorService.getVisitorData();
      setVisitors(data.map(visitor => ({
        ...visitor,
        isEditing: false,
        id: visitor.id || Math.random(),
        first_name: visitor.first_name || '',
        email: visitor.email || '',
        visit_count: visitor.visit_count || 0,
        last_visit: visitor.last_visit || visitor.timestamp
      })));
    } catch (error) {
      console.error('Error fetching visitors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: number) => {
    setVisitors(prev => prev.map(visitor => 
      visitor.id === id ? { ...visitor, isEditing: true } : visitor
    ));
  };

  const handleSave = async (visitor: EditingVisitor) => {
    try {
      await visitorService.updateVisitor(visitor.id, {
        first_name: visitor.first_name,
        email: visitor.email
      });
      setVisitors(prev => prev.map(v => 
        v.id === visitor.id ? { ...visitor, isEditing: false } : v
      ));
    } catch (error) {
      console.error('Error updating visitor:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this visitor?')) {
      try {
        await visitorService.deleteVisitor(id);
        setVisitors(prev => prev.filter(visitor => visitor.id !== id));
      } catch (error) {
        console.error('Error deleting visitor:', error);
      }
    }
  };

  const handleDownload = () => {
    const data = visitors.map(visitor => 
      `Name: ${visitor.first_name}\nEmail: ${visitor.email}\nVisits: ${visitor.visit_count}\nLast Visit: ${visitor.last_visit}\nFirst Visit: ${visitor.timestamp}\nUser Agent: ${visitor.userAgent}\n\n`
    ).join('---\n');

    const blob = new Blob([data], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitor-data-${new Date().toISOString()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const filteredVisitors = visitors
    .filter(visitor => {
      const searchTerm = filter.toLowerCase();
      return (
        (visitor.first_name?.toLowerCase() || '').includes(searchTerm) ||
        (visitor.email?.toLowerCase() || '').includes(searchTerm)
      );
    })
    .sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1;
      if (sortBy === 'timestamp') {
        return (new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) * order;
      }
      return ((a.visit_count || 0) - (b.visit_count || 0)) * order;
    });

  const stats = {
    totalVisitors: visitors.length,
    totalVisits: visitors.reduce((acc, curr) => acc + curr.visit_count, 0),
    activeToday: visitors.filter(v => 
      new Date(v.last_visit).toDateString() === new Date().toDateString()
    ).length
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div 
            className="bg-white rounded-lg shadow p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Visitors</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalVisitors}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </motion.div>

          <motion.div 
            className="bg-white rounded-lg shadow p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Visits</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalVisits}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </motion.div>

          <motion.div 
            className="bg-white rounded-lg shadow p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Today</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeToday}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
          </motion.div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search visitors..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'timestamp' | 'visit_count')}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="timestamp">Sort by Date</option>
                <option value="visit_count">Sort by Visits</option>
              </select>
              <button
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Visitors Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Visit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVisitors.map((visitor) => (
                <tr key={visitor.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {visitor.isEditing ? (
                      <input
                        type="text"
                        value={visitor.first_name}
                        onChange={(e) => setVisitors(prev => prev.map(v => 
                          v.id === visitor.id ? { ...v, first_name: e.target.value } : v
                        ))}
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900">
                        {visitor.first_name}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {visitor.isEditing ? (
                      <input
                        type="email"
                        value={visitor.email}
                        onChange={(e) => setVisitors(prev => prev.map(v => 
                          v.id === visitor.id ? { ...v, email: e.target.value } : v
                        ))}
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                    ) : (
                      <div className="text-sm text-gray-500">{visitor.email}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{visitor.visit_count}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(visitor.last_visit).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center gap-2">
                      {visitor.isEditing ? (
                        <>
                          <button
                            onClick={() => handleSave(visitor)}
                            className="text-green-600 hover:text-green-900"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setVisitors(prev => prev.map(v => 
                              v.id === visitor.id ? { ...v, isEditing: false } : v
                            ))}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(visitor.id)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(visitor.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 